/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: TelephonyServersActions.tsx
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Action buttons component for Telephony Servers DataGrid rows.
────────────────────────────────────────────────────────────────*/

import { Edit, Copy, Trash2, RefreshCw } from 'lucide-react';
import { useI18n } from '../../../hooks/useI18n';
import { TelephonyServer } from '../types';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';

interface TelephonyServersActionsProps {
  item: TelephonyServer;
  isCheckingStatus: boolean;
  onEdit: (item: TelephonyServer) => void;
  onDuplicate: (item: TelephonyServer) => void;
  onDelete: (item: TelephonyServer) => void;
  onCheckStatus: (item: TelephonyServer) => void;
}

export function TelephonyServersActions({
  item,
  isCheckingStatus,
  onEdit,
  onDuplicate,
  onDelete,
  onCheckStatus,
}: TelephonyServersActionsProps) {
  const { t } = useI18n();

  return (
    <div className="flex items-center gap-2">
      <SimpleTooltip content={t('telephonyServers.tooltips.editServer')} position="top">
        <button
          type="button"
          onClick={() => onEdit(item)}
          className="rounded p-1 text-blue-600 transition-colors hover:bg-blue-100/50 hover:text-blue-700 dark:text-blue-400 dark:hover:bg-blue-900/30 dark:hover:text-blue-300"
        >
          <Edit className="h-4 w-4" />
        </button>
      </SimpleTooltip>
      <SimpleTooltip content={t('telephonyServers.tooltips.duplicateServer')} position="top">
        <button
          type="button"
          onClick={() => onDuplicate(item)}
          className="rounded p-1 text-green-600 transition-colors hover:bg-green-100/50 hover:text-green-700 dark:text-green-400 dark:hover:bg-green-900/30 dark:hover:text-green-300"
        >
          <Copy className="h-4 w-4" />
        </button>
      </SimpleTooltip>
      {item.isLocal ? (
        <SimpleTooltip content={t('telephonyServers.tooltips.cannotDeleteLocal')} position="top">
          <span className="rounded p-1 text-muted-foreground/70">
            <Trash2 className="h-4 w-4 opacity-50" />
          </span>
        </SimpleTooltip>
      ) : (
        <SimpleTooltip content={t('telephonyServers.tooltips.deleteServer')} position="top">
          <button
            type="button"
            onClick={() => onDelete(item)}
            className="rounded p-1 text-red-600 transition-colors hover:bg-red-100/50 hover:text-red-700 dark:text-red-400 dark:hover:bg-red-900/30 dark:hover:text-red-300"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </SimpleTooltip>
      )}
      <SimpleTooltip
        content={
          isCheckingStatus
            ? t('telephonyServers.checkingStatus')
            : t('telephonyServers.tooltips.checkStatus')
        }
        position="top"
      >
        <button
          type="button"
          onClick={() => onCheckStatus(item)}
          disabled={isCheckingStatus}
          className="rounded p-1 text-primary transition-colors hover:bg-primary/10 hover:text-primary/80 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${isCheckingStatus ? 'animate-spin' : ''}`} />
        </button>
      </SimpleTooltip>
    </div>
  );
}

